/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import emojiRegex from "emoji-regex/RGI_Emoji.js";
import { hasCaps, hasHashtags } from "./../utils";

import Actions from "../../../../Actions";
import { useTranslation } from "react-i18next";
import { channelsByShortName } from "../../../../data/app__channels";
import { useCampaignContext } from "../../../../context/Campaign.context";

/**
 * Custom hook for validating teaser input text.
 * @param {string} teaserInputText - The teaser input text to be validated.
 * @param {string[]} bannedCharacters - Array of banned characters.
 * @param {boolean} disableEmojis - Flag indicating whether emojis are disabled.
 * @param {boolean} rejectUppercaseWords - Flag indicating whether uppercase words are rejected.
 * @param {boolean} rejectHashtags - Flag indicating whether hshtags are rejected.
 * @param {boolean} rejectPhoneNumbers - Flag indicating whether phone numbers are rejected.
 * @param {string[]} uppercaseWhitelist - Array of uppercase words to be whitelisted.
 * @param {string} channel - The channel identifier.
 * @param {Object[]} adErrors - Array of error objects.
 * @param {string} postDataKey - The key for post data that identifies the input.
 * @returns {{valid: boolean, error: string | undefined}} An object containing the validation result
 */
const useTeaserInputValidation = (
  teaserInputText,
  bannedCharacters,
  disableEmojis,
  rejectUppercaseWords,
  rejectPhoneNumbers,
  uppercaseWhitelist,
  channel,
  adErrors,
  postDataKey,
  isLoadingAutoGenerated,
  rejectHashtags
) => {
  const keyInputValidator = `${channel}-${postDataKey}`;
  const channelName = channel ? channelsByShortName[channel]?.channel_full_name : "";

  const { selectedChannels } = useCampaignContext();
  const { t } = useTranslation();
  const [errorInformed, setErrorInformed] = useState(adErrors.length > 0);

  const emitTextError = () => {
    const hasInputErrors = adErrors.some((error) => error.inputKey === keyInputValidator);
    if (!errorInformed) {
      setErrorInformed(true);
    }

    if (!hasInputErrors) {
      Actions.setAdErrors({
        inputKey: keyInputValidator,
        hasError: true,
        errorMessage: `${channelName} ${t("has an error in the text of the ad")}. \n ${t(
          "Please check and fix any issues to continue."
        )}`
      });
    }
  };

  // Remove error if input is now valid
  useEffect(() => {
    const hasInputErrors = adErrors.some((error) => error.inputKey === keyInputValidator);

    // errors that occurred but the channel was removed since.
    const nonSelectedErrors = adErrors
      .filter(
        (error) => !selectedChannels.some((channel) => error.inputKey.startsWith(`${channel}-`))
      )
      .map((error) => error.inputKey);

    nonSelectedErrors.forEach((errorKey) => {
      Actions.removeAdErrors(errorKey);
    });

    if (errorInformed && hasInputErrors && valid) {
      Actions.removeAdErrors(keyInputValidator);
    }
  }, [valid, teaserInputText, errorInformed, selectedChannels]);

  const { valid, error } = useMemo(() => {
    if (isLoadingAutoGenerated && !teaserInputText) return { valid: true };

    const repeatedNonWordCharactersRegex = /([^\w\s\n])\1/;
    const phoneNumberRegex = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/;

    if (!teaserInputText && channel !== "spotify") {
      const errorMessage = t("Text cannot be empty");
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (bannedCharacters?.length) {
      const characterErrors = bannedCharacters.filter((char) => teaserInputText.includes(char));
      const errorMessage = t("Invalid characters: {{chars}}", { chars: characterErrors.join("") });
      if (characterErrors?.length) {
        emitTextError();
        return { valid: false, error: errorMessage };
      }
    }

    if (disableEmojis && emojiRegex().exec(teaserInputText)) {
      const errorMessage = t("Emojis are not allowed");
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (teaserInputText.includes("...")) {
      const errorMessage = t("These characters '...' are not allowed");
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (rejectUppercaseWords && hasCaps(teaserInputText, uppercaseWhitelist)) {
      const errorMessage = t("Words with three or more capital letters are not allowed");
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (rejectHashtags && hasHashtags(teaserInputText)) {
      const errorMessage = t("Ads containing hashtags are not allowed");
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (repeatedNonWordCharactersRegex.test(teaserInputText)) {
      const repeatedChars = teaserInputText
        .match(repeatedNonWordCharactersRegex)
        .map((match) => match[0]);
      const errorMessage = t("Duplicate special characters: {{chars}}", {
        chars: repeatedChars.join(", ")
      });
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (rejectPhoneNumbers && phoneNumberRegex.test(teaserInputText)) {
      const errorMessage = t("Phone numbers are not allowed");
      emitTextError();
      return { valid: false, error: errorMessage };
    }

    if (errorInformed && adErrors.length === 0) {
      setErrorInformed(false);
    }

    return { valid: true };
  }, [
    bannedCharacters,
    teaserInputText,
    disableEmojis,
    rejectUppercaseWords,
    uppercaseWhitelist,
    rejectPhoneNumbers,
    errorInformed,
    isLoadingAutoGenerated
  ]);

  return { valid, error };
};

export default useTeaserInputValidation;
